import {
    normalizeMeasureAttributes,
    changeConstructionSystem,
    resolveMeasureDefinition,
    representationForDefinition,
    typeAttributeKeys,
    type ConstructionSystem,
} from "@treegram/viewer-core";
import MeasureType from "@/pages/takeoff/context/project-measure/models/measure-type.ts";
import rgbaToHexAndAlpha from "@/utils/colors/rgba-to-hex-and-alpha.ts";
import shouldConvertFromCentimetersToMeters from "@/pages/takeoff/layouts/measures/layouts/storey-measures/helpers";

interface EditMeasureTypeActionParams {
    measureType: MeasureType;
    formData: FormData;
}

interface EditMeasureTypeActionResult {
    success?: boolean;
    message?: string;
    updatedMeasureType?: MeasureType;
}

const editMeasureTypeAction = async ({
    measureType,
    formData,
}: EditMeasureTypeActionParams): Promise<EditMeasureTypeActionResult> => {
    const name = ((formData.get("name") as string) || "").trim();
    const color = formData.get("color") as string;

    if (name === "" || !color) {
        return { message: "Veuillez remplir tous les champs." };
    }

    const { hex, alpha } = rgbaToHexAndAlpha(color);

    const definition = resolveMeasureDefinition(
        measureType.measureRepresentationId,
        measureType.dataInformations,
    );
    const system = (formData.get("constructionSystem") || definition.system) as ConstructionSystem;
    let representationId: string;
    let values: Record<string, unknown>;
    try {
        representationId = representationForDefinition(definition.shape, system).id;
        values = changeConstructionSystem(
            measureType.measureRepresentationId,
            measureType.dataInformations ?? {},
            system,
        );
    } catch {
        return { message: "Système constructif incompatible avec cette forme." };
    }
    // Conserver les métadonnées, après retrait des propriétés métier devenues inapplicables.
    const dataInformations: Record<string, unknown> = {
        ...values,
        color: hex,
        alpha: alpha,
    };

    let allFieldsFilled = true;

    const dataInformationsFromMeasureType = measureType.dataInformations;
    if (measureType.dataFormatType && dataInformationsFromMeasureType) {
        typeAttributeKeys(representationId, values)
            .map((key) => [key, key === "symbol" || key === "color" ? "string" : "number"])
            .filter(([key]) => key !== "color" && key !== "alpha")
            .forEach(([key, type]) => {
                const val = formData.get(`dataFormatType_${key}`);

                if (typeof val !== "string" || val.trim() === "") {
                    allFieldsFilled = false;
                    return;
                }

                if (type === "number") {
                    let numVal = parseFloat(val);
                    if (shouldConvertFromCentimetersToMeters(key)) numVal /= 100;
                    if (!Number.isFinite(numVal) || numVal < 0 || (key !== "offset" && numVal === 0))
                        allFieldsFilled = false;
                    dataInformations[key] = numVal;
                } else {
                    dataInformations[key] = String(val);
                }
            });
    }

    if (
        definition.shape === "Point" &&
        !["circle", "square", "diamond"].includes(String(dataInformations.symbol))
    )
        return { message: "Symbole inconnu." };
    if (!allFieldsFilled) {
        return { message: "Veuillez remplir tous les champs." };
    }

    const updatedMeasureType = new MeasureType(
        measureType.measureTypeId,
        representationId,
        measureType.measureShapeId,
        name,
        normalizeMeasureAttributes(representationId, dataInformations),
    );

    return { success: true, updatedMeasureType };
};

export default editMeasureTypeAction;
